import {
  PolfEventHoleCompetitionTypeEnum,
  PolfEventRankingEnum,
  PolfEventStatusEnum,
} from '@polf/shared/codegen/types'
import { useLivePoll } from '@polf/shared/hooks'
import { matchMemberIds, sideIds } from '@polf/shared/match'
import { segmentForHole } from '@polf/shared/scoring'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import {
  namedOperations,
  useCurrentPlayerFlightMatchesQuery,
  useCurrentPlayerFlightScoresQuery,
  useCurrentPlayerHoleCompetitionsQuery,
  useCurrentPlayerScoreSubmitFlightMutation,
  useCurrentPlayerScoreUpdateMutation,
} from '../../__generated__/hooks'
import { useOnlineStatus } from '../../hooks/useOnlineStatus'
import type { CurrentPlayerCtx } from '../../PlayerShell'
import { isBestBallFlight } from '../../util/best-ball'
import { isBestWorst, isSegmented, usesChips } from '../../util/event-mode'
import { orderFlightForViewer } from '../../util/flight-order'
import { nextActionableHole } from '../../util/hole-sequence'
import ActionBar from './components/ActionBar'
import HoleCompPrompt from './components/HoleCompPrompt'
import HoleHeader from './components/HoleHeader'
import LockedBanner from './components/LockedBanner'
import SubmitSheet from './components/SubmitSheet'
import { useHoleSelector } from './hooks/useHoleSelector'
import BestBallLayout from './views/BestBallLayout'
import IndividualLayout from './views/IndividualLayout'
import MatchLayout from './views/MatchLayout'

/** Norwegian names for the formats a block can be played in. */
const SEGMENT_FORMAT_LABEL: Record<string, string> = {
  SCRAMBLE: 'Scramble',
  BESTBALL: 'Best ball',
  FOURSOME: 'Foursome',
  GOLF: 'Singel',
}

/**
 * Player score entry — per-hole, per-flight, the hottest path in the
 * whole product. Mockup reference: player-hole.jsx.
 *
 * Dispatcher: loads flight + side-game data, resolves the current hole,
 * computes shared state (locked, isYourEntry, allHolesScored etc.) and
 * picks the layout. Best-ball events use `BestBallLayout` (team-grouped
 * sections + contributor pill); everything else (Golf, POLF, Scramble)
 * uses `IndividualLayout` (flat list — Scramble entries are team rows
 * but the card shape is identical).
 *
 * Hooks live in `hooks/useHoleSelector.ts`; sub-components in
 * `components/`; layout variants in `views/`.
 */
const Score = () => {
  const { player, refetchPlayer } = useOutletContext<{
    player: CurrentPlayerCtx
    refetchPlayer: () => Promise<unknown>
  }>()
  const { playerKey = '' } = useParams<{ playerKey: string }>()
  // Shotgun flights open on their own start hole (not hole 1) on a fresh
  // load — the URL / sessionStorage still win once the captain has navigated.
  // A team member reaches the flight through their team, the same fallback
  // currentPlayerScoreSubmitFlight resolves the flight with.
  const flightStartHole =
    player.flight?.startHole?.number ??
    player.team?.flight?.startHole?.number ??
    null
  const startHoleNumber = flightStartHole ?? 1
  const { currentHole, setHole } = useHoleSelector(playerKey, startHoleNumber)
  const [submitOpen, setSubmitOpen] = useState(false)
  const online = useOnlineStatus()

  const { data, loading, refetch } = useCurrentPlayerFlightScoresQuery({
    fetchPolicy: 'cache-and-network',
  })
  // Flight scores + per-member hcp are server-recomputed: another flight member
  // entering scores, or an admin correcting a hcp/score, changes the
  // net/stableford + the all-holes-scored gating here. Poll so it reflects
  // without a reload. Own edits still land instantly via their mutations.
  // Once scoring is locked only a reopen can move these numbers, so the tick
  // slows to a check rather than a watch.
  useLivePoll(refetch, player.polfEvent.isReadOnly ? 60_000 : 15_000, true, {
    idleAfterMs: 5 * 60_000,
    idleIntervalMs: 60_000,
  })
  // On DRAFT→STARTED the score rows are generated on start, but this query can
  // still hold the empty pre-start cache for a beat (the event status arrives
  // via the shell's separate poll). Rather than hard-error, refetch a few times
  // and show a loading state until the scorecard lands. The budget is only spent
  // while online — a refetch that can't reach the network says nothing about
  // whether the scorecard exists.
  const [scorecardRetries, setScorecardRetries] = useState(0)
  const matchPlay = Boolean(player.polfEvent.matchPlay)
  // The match, when the event has one. Polled on the same cadence as the
  // scores it derives from — an opponent entering a hole moves the standing,
  // and the poll stack is gated + idle-backed-off (POL-16), so this is one
  // more relevance-gated tick rather than a new watcher.
  const { data: matchData, refetch: refetchMatch } =
    useCurrentPlayerFlightMatchesQuery({ fetchPolicy: 'cache-and-network' })
  useLivePoll(
    refetchMatch,
    player.polfEvent.isReadOnly ? 60_000 : 15_000,
    matchPlay,
    { idleAfterMs: 5 * 60_000, idleIntervalMs: 60_000 }
  )
  // Every match in the flight — the scorer enters for all of them.
  const allMatches = matchData?.currentPlayerFlightMatches ?? []

  // Side-games on the current hole — surfaced as a tap-through prompt
  // so the captain notices the comp without leaving the score flow.
  const { data: compsData } = useCurrentPlayerHoleCompetitionsQuery({
    variables: {
      holeCompetitionType: [
        PolfEventHoleCompetitionTypeEnum.Closest,
        PolfEventHoleCompetitionTypeEnum.Longest,
        PolfEventHoleCompetitionTypeEnum.Beatthepro,
      ],
    },
    fetchPolicy: 'cache-and-network',
  })
  const compsHere = useMemo(
    () =>
      (compsData?.currentPlayerHoleCompetitions ?? []).filter(
        (c) => c.hole.number === currentHole
      ),
    [compsData, currentHole]
  )

  const [updateScore, { loading: saving, error: saveError }] =
    useCurrentPlayerScoreUpdateMutation()
  // The mutation returns Boolean, so there is nothing for Apollo to merge —
  // write the lock into the cache by hand and let the broadcast carry it to
  // the shell, whose copy of the player is what `locked` reads.
  //
  // Name-form refetchQueries, not `{ query: DOC }`: the object form is routed
  // into `legacyQueryOptions` and always builds a THROWAWAY network-only
  // ObservableQuery, so `awaitRefetchQueries` waits on something the page is
  // not rendering from. The name matches the mounted watcher and warns in dev
  // if it ever stops matching. (`currentPlayer` is not listed here — the
  // confirm handler awaits the shell's own `refetchPlayer` for server truth.)
  const [submitFlight, { loading: submitting, error: submitError }] =
    useCurrentPlayerScoreSubmitFlightMutation({
      refetchQueries: [namedOperations.Query.currentPlayerFlightScores],
      awaitRefetchQueries: true,
      update: (cache) => {
        cache.modify({
          id: cache.identify({
            __typename: 'PolfEventPlayer',
            id: player.id,
          }),
          fields: {
            scoresSubmitted: () => true,
          },
        })
      },
    })

  // Stable references — `?? []` would produce a fresh empty array every
  // render and break downstream hook deps (Rules of Hooks).
  const flight = useMemo(
    () => data?.currentPlayerFlightScores ?? [],
    [data?.currentPlayerFlightScores]
  )
  // The card's length is the COURSE's, not one entry's. A split round's
  // entries each cover part of the round — a team entry holds only its
  // one-ball block — so reading it off flight[0] capped the round at six
  // holes.
  const totalHoles = useMemo(
    () => player.polfEvent.course?.holes?.length ?? 18,
    [player.polfEvent.course?.holes?.length]
  )

  // Who owns a row on THIS hole. Ownership is per hole in a split round —
  // the team plays the one-ball block and the players play the rest — so
  // resolving it once for the round picked an entry that has no row here and
  // reported the hole missing from your card. Every other format gives every
  // entry every hole, so this filters nothing there.
  const entriesOnHole = useMemo(
    () =>
      orderFlightForViewer(
        flight.filter((e) =>
          (e.scores ?? []).some((s) => s.hole.number === currentHole)
        ),
        player
      ),
    [flight, currentHole, player]
  )

  // Scramble events return TEAM rows (not player rows) from the API, so
  // matching on player.id alone returns nothing. Fall back to the team id,
  // and finally to the first entry that has the hole — the hole metadata
  // (par, index) is course-level and identical across entries.
  const yourEntry =
    entriesOnHole.find((e) => e.id === player.id) ??
    entriesOnHole.find((e) => e.id === player.team?.id) ??
    entriesOnHole[0] ??
    flight[0]

  const yourHole = useMemo(() => {
    if (!yourEntry) return null
    return (
      (yourEntry.scores ?? []).find((s) => s.hole.number === currentHole) ??
      null
    )
  }, [yourEntry, currentHole])

  const MAX_SCORECARD_RETRIES = 3
  const scorecardReady = flight.length > 0 && yourHole != null
  // Not DRAFT (rows should exist) but the scorecard isn't here yet → transient.
  const startedButNotReady =
    player.polfEvent.status !== PolfEventStatusEnum.Draft && !scorecardReady
  useEffect(() => {
    if (scorecardReady) {
      if (scorecardRetries !== 0) setScorecardRetries(0)
      return undefined
    }
    if (
      !startedButNotReady ||
      scorecardRetries >= MAX_SCORECARD_RETRIES ||
      !online
    ) {
      return undefined
    }
    const t = setTimeout(() => {
      refetch().catch(() => {
        /* transient — next render retries */
      })
      setScorecardRetries((r) => r + 1)
    }, 800)
    return () => clearTimeout(t)
  }, [scorecardReady, startedButNotReady, scorecardRetries, refetch, online])

  const handlePick = useCallback(
    async (scoreId: string, gross: number) => {
      try {
        await updateScore({
          variables: { input: { polfEventScoreId: scoreId, score: gross } },
        })
        // The match is computed server-side from the score rows, so a tap
        // changes it — but the mutation returns Boolean, so Apollo has nothing
        // to merge. Without this the standing only moved on the next poll,
        // which idles out to 60s mid-round: it read as a stale, flaky band.
        // One small gated query per tap is the honest cost of it being live.
        if (matchPlay) await refetchMatch()
      } catch {
        // Apollo error link surfaces auth failures; transient network
        // errors get retried on the next cache-and-network render.
      }
    },
    [updateScore, matchPlay, refetchMatch]
  )

  // A DRAFT event has no score rows yet (they're generated on start), so the
  // score grid can't render. Show a clear "not started" state instead of the
  // "hole not in your scorecard" fallback.
  if (player.polfEvent.status === PolfEventStatusEnum.Draft) {
    return (
      <section className="px-4 py-10 text-center text-ink-500 flex flex-col gap-2">
        <p className="font-display font-bold text-lg text-green-900">
          Runden har ikke startet
        </p>
        <p className="text-sm">
          Kom tilbake når kapteinen har startet runden, så åpnes scorekortet.
        </p>
      </section>
    )
  }

  if (loading && !data) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster scorekort…
      </section>
    )
  }
  // Freshly started but the scorecard hasn't arrived yet — keep showing loading
  // (and refetching, above) rather than a hard error, until retries run out.
  if (startedButNotReady && scorecardRetries < MAX_SCORECARD_RETRIES) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Laster scorekort…
      </section>
    )
  }
  // Only an empty cache is an error — a failed poll keeps the last good flight
  // on screen (same care as PlayerShell).
  if (!flight.length) {
    return online ? (
      <section className="px-4 py-8 text-center text-flag">
        Fant ikke flighten din. Prøv å laste siden på nytt.
      </section>
    ) : (
      <section className="px-4 py-8 text-center text-ink-500">
        Ingen nett. Scorekortet lastes så snart du er tilkoblet igjen.
      </section>
    )
  }
  if (!yourHole) {
    return (
      <section className="px-4 py-8 text-center text-ink-500">
        Hull {currentHole} er ikke i scorekortet ditt.
      </section>
    )
  }

  // "All scored on current hole" gates the per-step advance button.
  // "All 18 holes scored across the flight" is the ONLY signal that
  // flips the CTA to submit — the old `currentHole === 18` heuristic
  // misfired on partial rounds and didn't survive shotgun starts where
  // the flight's last hole is startHole - 1.
  const allScoredCurrentHole =
    entriesOnHole.length > 0 &&
    entriesOnHole.every(
      (e) =>
        (e.scores ?? []).find((s) => s.hole.number === currentHole)?.score !=
        null
    )
  const allHolesScored = flight.every((e) =>
    (e.scores ?? []).every((s) => s.score != null)
  )

  // `nextActionableHole` walks the play sequence and wraps past the
  // end of rotation when not all holes are scored — fixes the bug
  // where shotgun flights stalled on their last-played hole with a
  // dead "Bekreft" CTA. Falls back to the first unscored hole in the
  // rotation so the captain always has a target to advance to.
  const isHoleCompleteForFlight = (holeNumber: number) =>
    // Owners only: an entry with no row on this hole has nothing to complete,
    // and requiring one stalled the round on a split card's first hole.
    flight
      .filter((e) => (e.scores ?? []).some((s) => s.hole.number === holeNumber))
      .every(
        (e) =>
          (e.scores ?? []).find((s) => s.hole.number === holeNumber)?.score !=
          null
      )
  const nextHole = nextActionableHole(
    startHoleNumber,
    currentHole,
    totalHoles,
    isHoleCompleteForFlight
  )
  const locked = player.scoresSubmitted || player.polfEvent.isReadOnly
  const showChips = usesChips(player.polfEvent.mode)
  const isNet = player.polfEvent.ranking === PolfEventRankingEnum.NetStrokes
  // One lookup: which block this hole belongs to. Both the layout and the
  // header band follow the BLOCK's format rather than the event's — a split
  // round's best-ball block is team-shaped like any best ball, and rendering
  // it flat made it indistinguishable from the singles block beside it.
  //
  // isBestBallFlight stays the answer for every other format. It is a
  // whole-round test and would answer true across all 18 holes here, so the
  // segment check is gated in front of it rather than folded into it.
  //
  // A plain derivation, not a hook: this sits after the page's early returns.
  const segmented = isSegmented(player.polfEvent.mode)
  const blockSegment = segmented
    ? segmentForHole(player.polfEvent.segments ?? [], currentHole)
    : null
  const bestBall = segmented
    ? blockSegment?.format === 'BESTBALL'
    : !isBestWorst(player.polfEvent.mode) && isBestBallFlight(flight)
  // Only the block being played. A split round's flight holds three or four
  // matches at once, and the other blocks' are not on this hole — rendering
  // them all would put three standings above a card that decides one.
  const matchesHere = blockSegment
    ? allMatches.filter((m) => m.segmentOrdinal === blockSegment.ordinal)
    : allMatches
  // Yours on top, for the same reason the cards are: a singles block runs two
  // matches side by side, and the scorer's own standing should not move
  // depending on which pairing the server listed first.
  const yourMatchIds = new Set(
    [player.id, player.team?.id].filter((id): id is string => id != null)
  )
  const matches = [...matchesHere].sort((a, b) => {
    const mine = (m: (typeof matchesHere)[number]) =>
      [...sideIds(m), ...matchMemberIds(m)].some((id) => yourMatchIds.has(id))
        ? 0
        : 1
    return mine(a) - mine(b)
  })

  // Told, not inferred from the shape of the cards below.
  const block = blockSegment
    ? {
        label: SEGMENT_FORMAT_LABEL[blockSegment.format] ?? blockSegment.format,
        fromHole: blockSegment.fromHole,
        toHole: blockSegment.toHole,
      }
    : null
  // "You" matches player id (Golf/BestBall) OR team id (Scramble — entries
  // are teams there, so the captain's row is found via team id).
  const isYourEntry = (entryId: string) =>
    entryId === player.id || entryId === player.team?.id

  // Per-action feedback, derived from the mutation error state (the timeout
  // link guarantees a hung request resolves, so this can't stick forever).
  // Ambient offline context lives in the TopBar banner; this is the honest
  // "that didn't save" message for the specific tap.
  const saveMsg = saveError
    ? 'Kunne ikke lagre slaget. Sjekk nettet og prøv igjen.'
    : null
  const submitMsg = submitError
    ? 'Kunne ikke levere scorekortet. Sjekk nettet og prøv igjen.'
    : null

  return (
    <section>
      <HoleHeader
        hole={yourHole.hole}
        total={totalHoles}
        block={block}
        onPrev={() => setHole(currentHole - 1)}
        onNext={() => setHole(currentHole + 1)}
      />
      <HoleCompPrompt
        playerKey={playerKey}
        comps={compsHere}
        showChips={showChips}
      />
      {locked ? (
        <LockedBanner eventIsReadOnly={player.polfEvent.isReadOnly} />
      ) : null}

      {saveMsg ? (
        <div
          role="alert"
          className="mx-4 mt-2 rounded-lg bg-flag text-paper px-3 py-2 text-sm font-semibold"
        >
          {saveMsg}
        </div>
      ) : null}

      {matchPlay && matches.length > 0 ? (
        // Match play regroups the flight by pairing, so who plays whom is
        // visible in the shape of the screen and each standing sits in the
        // block it belongs to.
        <MatchLayout
          flight={flight}
          matches={matches}
          currentHole={currentHole}
          courseHoles={player.polfEvent.course?.holes ?? []}
          startHole={flightStartHole}
          block={blockSegment}
          showChips={showChips}
          isNet={isNet}
          isYourEntry={isYourEntry}
          onSetScore={handlePick}
          saving={saving || locked}
        />
      ) : bestBall ? (
        <BestBallLayout
          flight={entriesOnHole}
          partialCard={segmented}
          currentHole={currentHole}
          showChips={showChips}
          isNet={isNet}
          isYourEntry={isYourEntry}
          onSetScore={handlePick}
          saving={saving || locked}
        />
      ) : (
        <IndividualLayout
          flight={entriesOnHole}
          partialCard={segmented}
          currentHole={currentHole}
          showChips={showChips}
          isNet={isNet}
          isYourEntry={isYourEntry}
          onSetScore={handlePick}
          saving={saving || locked}
        />
      )}

      {locked ? null : (
        <ActionBar
          allHolesScored={allHolesScored}
          allScoredCurrentHole={allScoredCurrentHole}
          submitting={submitting}
          nextHole={nextHole}
          onSubmit={() => setSubmitOpen(true)}
          onAdvance={setHole}
        />
      )}

      <SubmitSheet
        open={submitOpen}
        submitting={submitting}
        errorMessage={submitMsg}
        onClose={() => setSubmitOpen(false)}
        onConfirm={async () => {
          try {
            await submitFlight()
            await refetchPlayer()
            setSubmitOpen(false)
          } catch {
            // Apollo error link surfaces auth/network failures; keep
            // the sheet open so the user can retry.
          }
        }}
      />
    </section>
  )
}

export default Score
